import React, {useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {fetchDropoutDictionary} from 'api/documents/create';
import {exportWithdrawalAuditLog, fetchWithdrawalAuditLog} from 'api/withdrawalAudit';
import {auditColumns} from 'components/common/AuditLog/columns';
import {AuditExportControl} from 'components/common/AuditLog/ExportControl';
import {AuditNavigation} from 'components/common/AuditLog/Navigation';
import {AuditPeriodFilter} from 'components/common/AuditLog/PeriodFilter';
import {AuditReference} from 'components/common/AuditLog/Reference';
import {PageContent, Toolbar} from 'components/common/AuditLog/styled';
import {useAuditLog} from 'components/common/AuditLog/useAuditLog';
import {Page} from 'components/common/Page';
import {formatLocalDateTime, translateUserRole} from 'components/pages/RecallAudit/utils';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PERMISSION_ALIASES} from 'core/constants/permissionAliases';
import {RestError, showRestError} from 'core/errors';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {DropoutDictionaryResponse} from 'types/documents';
import {WITHDRAWAL_AUDIT_EVENTS} from 'types/withdrawal-audit';

import {mapWithdrawalAuditFilters} from './filters';

export {WithdrawalAuditDetailPage} from './Detail';

export const WithdrawalAuditLogPage = () => {
    const {t} = useTranslation();
    const permissions = useStore(permissions$);
    const [dictionary, setDictionary] = useState<DropoutDictionaryResponse | null>(null);
    const {
        filters,
        page,
        size,
        data,
        pending,
        setPage,
        setSize,
        changeFilters,
        resetFilters,
    } = useAuditLog(fetchWithdrawalAuditLog, mapWithdrawalAuditFilters);
    useEffect(() => {
        let active = true;
        fetchDropoutDictionary()
            .then((value) => {
                if (active) setDictionary(value);
            })
            .catch((error) => {
                if (active) showRestError(error as RestError);
            });
        return () => {
            active = false;
        };
    }, []);
    const fields = auditColumns(
        t,
        WITHDRAWAL_AUDIT_EVENTS.map((value) => ({
            value,
            label: t(`pages.withdrawalAudit.events.${value}`),
        })),
    ).map((field) =>
        field.id === 'eventTimestamp' ? {...field, filter: <AuditPeriodFilter />} : field,
    );
    fields.push({
        id: 'documentId',
        header: t('pages.withdrawalAudit.fields.documentId'),
        filter: <Input />,
        width: 240,
    });
    const selects = [
        {
            id: 'withdrawalReason',
            values: dictionary?.reasons || [],
            translation: 'dictionaries.documents.DropOut.reasons',
        },
        {
            id: 'sourceFormation',
            values: dictionary?.formationProcesses || [],
            translation: 'pages.withdrawalAudit.sources',
        },
        {
            id: 'salesChannel',
            values: dictionary?.salesChannels || [],
            translation: 'dictionaries.documents.DropOut.salesChannels',
        },
    ];
    fields.push(
        ...selects.map(({id, values, translation}) => ({
            id,
            header: t(`pages.withdrawalAudit.fields.${id}`),
            width: 220,
            filter: (
                <Select
                    isClearable
                    isDisabled={!dictionary}
                    options={values.map((value) => ({
                        value,
                        label: t(`${translation}.${value}`, {defaultValue: value}),
                    }))}
                />
            ),
        })),
    );
    const rows = (data?.data || []).map((entry) => ({
        ...entry,
        entry,
        eventId: (
            <Link to={generatePath(ROUTE.WITHDRAWAL_AUDIT_DETAIL, {id: entry.eventId})}>
                {entry.eventId}
            </Link>
        ),
        documentId: <AuditReference field="documentId" value={entry.documentId} />,
        ...Object.fromEntries(
            selects.map(({id, translation}) => {
                const value = entry[id as keyof typeof entry];
                return [
                    id,
                    value ? t(`${translation}.${value}`, {defaultValue: String(value)}) : '—',
                ];
            }),
        ),
        eventType: t(`pages.withdrawalAudit.events.${entry.eventType}`, {
            defaultValue: entry.eventType,
        }),
        eventTimestamp: formatLocalDateTime(entry.eventTimestamp),
        userRole: translateUserRole(t, entry.userRole),
        userFullName: entry.userFullName || entry.userId || '—',
        organizationName: entry.organizationName ?? entry.organizationId ?? '—',
        organizationTin: entry.organizationTin || '—',
    }));
    return (
        <Page title={t('menu.activityLog')}>
            <PageContent>
                <AuditNavigation>
                    <Toolbar>
                        {permissions.includes(PERMISSION_ALIASES.withdrawalAuditLogExport) && (
                            <AuditExportControl
                                fileName="withdrawal-audit"
                                initialPeriod={filters.eventTimestamp as [Date | null, Date | null]}
                                onExport={(format, period) => {
                                    const params = mapWithdrawalAuditFilters({
                                        ...filters,
                                        eventTimestamp: period,
                                    });
                                    return exportWithdrawalAuditLog({
                                        ...params,
                                        from: params.from!,
                                        to: params.to!,
                                        page,
                                        size,
                                        format,
                                    });
                                }}
                            />
                        )}
                    </Toolbar>
                </AuditNavigation>
                <Table
                    hideSetting
                    columns={mapTableColumns(fields, filters, changeFilters, resetFilters)}
                    data={rows}
                    pageIndex={page}
                    pageCount={data?.totalPages}
                    totalRowsCount={data?.totalElements}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                        setSize(value);
                        setPage(0);
                    }}
                    isLoading={pending}
                />
            </PageContent>
        </Page>
    );
};
